<template>
  <div class="compare-layouts">
    <div class="compare-header">
      <h1 class="text-2xl font-bold text-center py-4">Layout Comparison</h1>
      <div class="flex justify-center space-x-8 mb-4">
        <div class="text-center">
          <div class="text-sm font-medium text-gray-700">Original Layout</div>
          <div class="text-xs text-gray-500">Uptime Bars Chart</div>
        </div>
        <div class="text-center">
          <div class="text-sm font-medium text-gray-700">Variant Layout</div>
          <div class="text-xs text-gray-500">Response Time Chart</div>
        </div>
      </div>
    </div>

    <div class="compare-container">
      <!-- Left side: Original StatusPage -->
      <div class="compare-panel">
        <div class="panel-label">Original (Uptime Bars)</div>
        <div class="panel-content">
          <StatusPage
            v-bind="sharedProps"
            :key="`original-${propsKey}`"
          />
        </div>
      </div>

      <!-- Right side: StatusPageVariant -->
      <div class="compare-panel">
        <div class="panel-label">Variant (Response Time)</div>
        <div class="panel-content">
          <StatusPageVariant
            v-bind="sharedProps"
            :key="`variant-${propsKey}`"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import StatusPage from './StatusPage.vue'
import StatusPageVariant from './StatusPageVariant.vue'

const route = useRoute()

const props = defineProps<{
  slug?: string
  isDomain?: string
  domain?: string
}>()

// Create a key that changes when props change to force re-render
const propsKey = computed(() => {
  return `${props.slug || ''}-${props.isDomain || ''}-${props.domain || ''}`
})

// Shared props for both components
const sharedProps = computed(() => ({
  slug: props.slug,
  isDomain: props.isDomain,
  domain: props.domain
}))
</script>

<style scoped>
.compare-layouts {
  min-height: 100vh;
  background-color: #f9fafb;
}

.compare-header {
  background-color: white;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.compare-container {
  display: flex;
  min-height: calc(100vh - 120px);
}

.compare-panel {
  flex: 1;
  position: relative;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
}

.compare-panel:last-child {
  border-right: none;
}

.panel-label {
  position: sticky;
  top: 0;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  z-index: 5;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  height: calc(100vh - 160px);
}

/* Override max-width constraints to fit side-by-side */
.compare-panel .status-page {
  max-width: none !important;
}

.compare-panel main {
  max-width: none !important;
  padding-left: 16px !important;
  padding-right: 16px !important;
}

.compare-panel header .max-w-4xl {
  max-width: none !important;
  padding-left: 16px !important;
  padding-right: 16px !important;
}

/* Reduce padding and margins for better fit */
.compare-panel .px-6 {
  padding-left: 16px !important;
  padding-right: 16px !important;
}

.compare-panel .py-8 {
  padding-top: 24px !important;
  padding-bottom: 24px !important;
}

/* Ensure tooltips and modals work properly in the context */
.compare-panel .fixed {
  position: fixed !important;
}

/* Make charts and components fit better */
.compare-panel .response-time-chart-container,
.compare-panel .uptime-chart-container {
  width: 100% !important;
}

/* Responsive design for smaller screens */
@media (max-width: 1200px) {
  .compare-container {
    flex-direction: column;
    height: auto;
  }

  .compare-panel {
    flex: none;
    height: 50vh;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }

  .panel-content {
    height: 50vh;
  }

  .compare-panel main {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }
}

@media (max-width: 768px) {
  .compare-header h1 {
    font-size: 1.25rem;
  }

  .compare-panel main {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  .panel-content {
    height: 40vh;
  }
}
</style>